import { Card, CardContent, CardHeader, CardProps, styled } from '@mui/material'
import { FC, ReactNode } from 'react'

import { H4, Span } from '../Typography'

export interface SimpleCardProps extends Omit<CardProps, 'title'> {
  title?: ReactNode
  subtitle?: ReactNode
  childIcon?: ReactNode
  children?: ReactNode
}

const CardRoot = styled(Card)(({ theme }) => ({
  height: '100%',
  padding: theme.spacing(2),
  overflow: 'visible', // Evita que se corten menús o selects desplegables

  '& .MuiCardHeader-root': {
    padding: 0,
    paddingBottom: theme.spacing(2.5), // Espacio limpio y constante hacia el contenido

    '& .MuiCardHeader-avatar': {
      // --- MEJORA: Tamaño y alineación del ícono ---
      marginRight: theme.spacing(1),
      color: theme.palette.primary.main,
      marginTop: 0, // Reiniciamos el marginTop anterior
      display: 'flex', // Asegura alineación interna perfecta
      alignItems: 'center',

      // Seleccionamos específicamente los íconos (SVG o FontIcon) dentro del avatar
      '& > svg, & > .material-icons, & > .MuiIcon-root': {
        fontSize: '1.7rem', // Un tamaño más sutil (aprox 20px). Ajustar si es necesario.
      },
    },

    // --- MEJORA: Alineación vertical de los textos ---
    '& .MuiCardHeader-content': {
      alignSelf: 'center', // Centra verticalmente el bloque de texto con el ícono
    },

    '& .MuiCardHeader-subheader': {
      marginTop: '-2px', // Pequeño ajuste para pegarlo más al título
    },
  },

  '& .MuiCardContent-root': {
    padding: 0,
    '&:last-child': {
      paddingBottom: 0, // Invalida el padding inferior por defecto de MUI
    },
  },

  '& .MuiAvatar-root': {
    backgroundColor: theme.palette.primary.light,
  },
}))

/**
 * SimpleCard: Contenedor tipo tarjeta limpio, optimizado y visualmente mejorado.
 * @author isi-template
 */
const SimpleCard: FC<SimpleCardProps> = ({
  children,
  title,
  subtitle,
  childIcon,
  variant = 'outlined',
  ...others
}) => {
  const titleRoot = typeof title === 'string' ? <H4>{title}</H4> : title
  const subHeader =
    typeof subtitle === 'string' ? (
      <Span sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{subtitle}</Span>
    ) : (
      subtitle
    )

  const hasHeader = Boolean(title || subtitle)

  return (
    <CardRoot variant={variant} {...others}>
      {hasHeader && <CardHeader avatar={childIcon} title={titleRoot} subheader={subHeader} />}

      <CardContent>{children}</CardContent>
    </CardRoot>
  )
}

export default SimpleCard
